import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import {batchInChunks} from "app/services/domain/base.service";
import {SbomListComponent} from "app/appsecops/sbom/list-sbom/list-sbom.component";
import * as Models from "app/services/proxy/model/models";

import {ChartColorUtilities} from "framework/ui/charting/core/colors";
import {KeyValueChip} from "framework/ui/chips/key-value-chip-list.component";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {Lookup} from "framework/services/utils.service";
import {Future, mapInParallel} from "framework/utils/concurrency";

@Injectable()
export class SbomService
{
    public static readonly NAME_REGEX_PATTERN: string                         = "^[0-9a-zA-Z][a-zA-Z0-9_.\\-@ ]*$";
    public sbomLookup: Lookup<Models.AppSecopsSbomDefinition>                 = {};
    private readonly m_sbomByTenant: Lookup<Models.AppSecopsSbomDefinition[]> = {};

    constructor(public api: ApiService)
    {}

    public async createDefinition(model: Models.AppSecopsSbomDefinition): Promise<Models.AppSecopsSbomDefinition>
    {
        return await this.api.appSecOps.createSbomDefinition(
            Models.AppSecopsSbomDefinitionRequest.newInstance(model)
        );
    }

    public async updateDefinition(model: Models.AppSecopsSbomDefinition,
                                  withoutMapping: boolean = false): Promise<Models.AppSecopsSbomDefinition>
    {

        let updatePayload: Partial<Models.AppSecopsSbomDefinition>;

        if (withoutMapping)
        {
            // Exclude resource_mapping_details
            const {
                      resource_mapping_details,
                      ...rest
                  }       = model;
            updatePayload = rest;
        }
        else
        {
            updatePayload = model;
        }
        return await this.api.appSecOps.updateSbomDefinition(
            model.sbom_definition_id,
            Models.AppSecopsSbomDefinitionUpdateRequest.newInstance(updatePayload)
        );
    }

    async listAllSbomDefinition(listReq: Models.AppSecopsSbomDefinitionsListRequest): Promise<string[]>
    {
        let context = Models.ListContext.newInstance({
                                                         batch_offset: 0,
                                                         batch_size  : 200,
                                                         total       : 0
                                                     });

        const result: string [] = [];
        while (context)
        {
            listReq.list_context = context;
            let resp             = await this.api.appSecOps.listSbomDefinitions(listReq);
            result.push(...resp.results);
            context = resp.next_list_context;
        }

        return result;
    }

    public async getDefinition(sbomId: string,
                               forceReload: boolean = false): Promise<Models.AppSecopsSbomDefinition>
    {
        if (!forceReload && this.sbomLookup[sbomId]) return this.sbomLookup[sbomId];

        const model               = await this.api.appSecOps.getSbomDefinition(sbomId);
        this.sbomLookup[sbomId]   = model;
        return model;
    }

    public async getFlat(sbomId: string,
                         forceReload: boolean = false): Promise<SbomFlat>
    {
        const model = await this.getDefinition(sbomId, forceReload);
        const flat  = new SbomFlat(null as any, model);

        if (model.release_version_id)
        {
            flat.releaseVersion = await this.api.appSecOps.getSbomVersion(model.release_version_id);
        }

        return flat;
    }

    public async sbomDefinitionBatch(ids: string[]): Promise<Models.AppSecopsSbomDefinition[]>
    {
        return batchInChunks(ids,
                             (chunk) => this.api.appSecOps.batchSbomDefinitions(Models.BatchRequest.newInstance({ids: chunk})),
                             1000);
    }

    public async getAll(listReq: Models.AppSecopsSbomDefinitionsListRequest): Promise<void>
    {
        const ids = await this.listAllSbomDefinition(listReq);
        if (ids.length)
        {
            const batchResponse = await this.sbomDefinitionBatch(ids);
            for (let i = 0; i < ids.length; i++) this.sbomLookup[ids[i]] = batchResponse[i];
        }
    }

    public async getAllForTenant(tenantId: string)
    {
        try
        {
            if (!this.m_sbomByTenant[tenantId])
            {
                let listReq = Models.AppSecopsSbomDefinitionsListRequest.newInstance({
                                                                                         filters: Models.AppSecopsSbomDefinitionsFilter.newInstance({
                                                                                                                                                        tenant_id: tenantId
                                                                                                                                                    })
                                                                                     });
                const ids   = await this.listAllSbomDefinition(listReq);
                let batch;
                if (ids.length)
                {
                    batch = await this.sbomDefinitionBatch(ids);
                }

                if (batch) this.m_sbomByTenant[tenantId] = batch;
            }
        }
        finally
        {
            return this.m_sbomByTenant[tenantId];
        }
    }

    public resetTenantCache(tenantId: string)
    {
        this.m_sbomByTenant[tenantId] = null;
    }

    public parseDate(date: Date): string
    {
        if (date)
        {
            return MomentHelper.parse(date)
                               .format("DD MMM YYYY, hh:mm A");
        }
        return "NA";
    }

    public getDiffSummary(summary: SbomDiffSummary): DiffSummaryLabelValue[]
    {
        const isComponents = summary.type === SbomDiffSummaryType.Components;
        const removedLabel = isComponents ? "Deleted" : "Resolved";

        const diffSummaryObj: DiffSummaryLabelValue[] = [
            {
                label: `Total (${summary.total.length})`,
                key  : "total",
                value: summary.total
            },
            {
                label: `Added (${summary.added.length})`,
                key  : "added",
                value: summary.added
            },
            {
                label: `${removedLabel} (${summary.removed.length})`,
                key  : "removed",
                value: summary.removed
            }
        ];
        if (isComponents)
        {
            diffSummaryObj.splice(1, 0, {
                label: `Upgraded (${summary.upgraded.length})`,
                key  : "upgraded",
                value: summary.upgraded
            });
        }
        return diffSummaryObj;
    }

    public parserStatusColor(parserStatus: Models.SbomParsingStatus): string
    {
        if (!parserStatus)
        {
            return ChartColorUtilities.getColorById("Severity Colors", "unknown").color;
        }

        let color: string;
        switch (parserStatus)
        {
            case Models.SbomParsingStatus.error:
                color = ChartColorUtilities.getColorById("Severity Colors", "critical").color;
                break;

            case Models.SbomParsingStatus.in_progress:
                color = ChartColorUtilities.getColorById("Severity Colors", "high").color;
                break;

            default:
                color = ChartColorUtilities.getColorById("Severity Colors", "low").color;
                break;
        }
        return color;
    }

    public parserStatusLabel(parserStatus: Models.SbomParsingStatus): string
    {
        switch (parserStatus)
        {
            case Models.SbomParsingStatus.error:
                return "Failed";

            case Models.SbomParsingStatus.success:
                return "Successful";

            case Models.SbomParsingStatus.in_progress:
                return "In Progress";

            default:
                return parserStatus || "Unknown";
        }
    }

    public async loadParserStatusBatch(sbomFlats: SbomFlat[]): Promise<void>
    {
        await mapInParallel(sbomFlats, async flat =>
        {
            try
            {
                const versions                                                    = await this.api.appSecOps.listSbomVersions(flat.model.sbom_definition_id);
                flat.definitionlatestVersionLookup[flat.model.sbom_definition_id] = versions[0];
            }
            catch
            {
                //Ignore errors, continue
            }
        });
    }
}

export class SbomFlat
{
    public versionInfoPromise                            = new Future<void>();
    public applicationProjectPromise                     = new Future<void>();
    public releaseVersion: Models.AppSecopsSbomVersionResponse;
    public parserVersion: Models.AppSecopsSbomVersion;
    public applications: string[];
    public project: string;
    public sbom: Models.Attachment;
    public dates: SbomDates                              = {
        versionCreated: "",
        generated     : ""
    };
    public sbomComponentDiffSummary: SbomDiffSummary;
    public sbomVulnerabilityDiffSummary: SbomDiffSummary;
    public parserStatus: SbomParserStatusInfo;
    public definitionlatestVersionLookup: Lookup<string> = {};
    public sbomVersionVulnerabilityIds: SbomVersionVulnerabilityIds;

    constructor(public readonly app: SbomListComponent,
                public model: Models.AppSecopsSbomDefinition)
    {}

    get isReady(): boolean
    {
        return !Object.values(this)
                      .some(value =>
                                value instanceof Future && !value.isResolved()
                      );
    }

    get versionNumber(): string
    {
        return this.releaseVersion ? `V${this.releaseVersion.version_number}.0` : "";
    }

    get showDeleteMapping(): boolean
    {
        return !!this.model.resource_mapping_details?.mapped_resources?.length && (this.model.resource_mapping_details?.mapped_resources || []).some(r => r.mapping_type
                                                                                                                                                          == Models.AppSecopsSbomResourceMappingType.manual)
               && this.isParserStatusCompleted;
    }

    get isParserStatusCompleted(): boolean
    {
        return this.releaseVersion?.parser_status?.status == Models.SbomParsingStatus.success;
    }

    get isParserStatusInProgress(): boolean
    {
        return this.parserVersion?.parser_status?.status == Models.SbomParsingStatus.in_progress;
    }

    get parserStatusLabel(): string
    {
        return this.parserStatus?.label || "";
    }

    get buildId(): string
    {
        return this.releaseVersion?.build_id || "";
    }

    get name(): string
    {
        return this.model.name || "";
    }

    get description(): string
    {
        return this.model.description || "";
    }

    get tags(): KeyValueChip[]
    {
        if (this.model.tags?.length)
        {
            return this.model.tags?.map(tag => new KeyValueChip(tag.key, tag.value, [], ":"));
        }
        return [];
    }

    get generatedDate(): string
    {
        return this.dates.generated || "";
    }

    get versionCreatedDate(): string
    {
        return this.dates.versionCreated || "";
    }

    get hideVersionDetails(): boolean
    {
        return !this.model.release_version_id;
    }

    get tagsDetailed(): string
    {
        if (this.model.tags?.length)
        {
            return this.model.tags?.map(tag => `${tag.key}: ${tag.value}`)
                       .join(",");
        }
        return "";
    }

    get mappedResources(): string[]
    {
        if (this.model.resource_mapping_details?.mapped_resources?.length)
        {
            return this.model.resource_mapping_details?.mapped_resources?.map(resource => resource.resource_id);
        }
        return [];
    }

    get vulnerabilityDiffCount(): string
    {
        if (this.sbomVulnerabilityDiffSummary && !this.isParserStatusInProgress)
        {
            return this.getDiffSummaryValues(this.sbomVulnerabilityDiffSummary);
        }
        return "";
    }

    get componentDiffCount(): string
    {
        if (this.sbomComponentDiffSummary && !this.isParserStatusInProgress)
        {
            return this.getDiffSummaryValues(this.sbomComponentDiffSummary);
        }
        return "";
    }

    public getDiffSummaryValues(diffSummary: SbomDiffSummary): string
    {
        return this.app.app.domain.sbomService.getDiffSummary(diffSummary)
                   .map(summary => summary.label)
                   .join(", ");
    }

    public updateVersionDetails(version: Models.AppSecopsSbomVersionResponse[],
                                attachments: Models.Attachment[] = null,
                                diffSummary?: Models.AppSecopsSbomVersionDifference[])
    {
        this.releaseVersion = version.find(version => version.sbom_version_id == this.model.release_version_id);
        this.parserVersion  = version.find(version => version.sbom_version_id == this.definitionlatestVersionLookup[version.sbom_definition_id]);
        if (this.releaseVersion)
        {
            const versionDiffSummary = diffSummary?.find(diff => diff?.base_version_id == this.releaseVersion?.sbom_version_id) || new Models.AppSecopsSbomVersionDifference();
            this.dates.generated     = this.app.app.domain.sbomService.parseDate(this.releaseVersion.metadata?.bom_created_at);
            if (attachments?.length)
            {
                this.sbom = attachments.find(attachment => attachment.id == this.releaseVersion.attachment_id);
            }
            this.updateDiffSummary(versionDiffSummary);
        }
        if (this.parserVersion)
        {
            if (this.parserVersion.parser_status?.status == Models.SbomParsingStatus.in_progress)
            {
                this.app.startParserPolling(this);
            }
            this.parserVersionDetailsUpdate();
        }
        this.app.markForCheck();
    }

    public parserVersionDetailsUpdate()
    {
        this.parserStatus         = {
            color         : this.app.app.domain.sbomService.parserStatusColor(this.parserVersion.parser_status?.status),
            label         : this.app.app.domain.sbomService.parserStatusLabel(this.parserVersion.parser_status?.status),
            secondaryLabel: `Version V${this.parserVersion.version_number}.0`,
            message       : this.parserVersion.parser_status?.message || ""
        };
        this.dates.versionCreated = this.app.app.domain.sbomService.parseDate(this.parserVersion?.created_at);
        this.app.markForCheck();
    }

    public async applyParserStatusUpdate(versionResponse: Models.AppSecopsSbomVersionResponse): Promise<void>
    {
        try
        {
            this.versionInfoPromise                                           = new Future<void>();
            this.parserVersion                                                = versionResponse;
            this.definitionlatestVersionLookup[this.model.sbom_definition_id] = this.parserVersion.sbom_version_id;
            await this.updateMappingDetails();
            if (this.model.release_version_id)
            {
                this.releaseVersion               = await this.app.app.domain.apis.appSecOps.getSbomVersion(this.model.release_version_id);
                const versionRequest              = Models.ObjectIdBatchRequest.newInstance({
                                                                                                ids: [
                                                                                                    this.model.release_version_id,
                                                                                                    this.parserVersion.sbom_version_id
                                                                                                ]
                                                                                            });
                const [versionBatch, diffSummary] = await Promise.all([
                                                                          this.app.app.domain.apis.appSecOps.batchSbomVersions(versionRequest),
                                                                          this.app.app.domain.apis.appSecOps.batchSbomVersionDifference(Models.ObjectIdBatchRequest.newInstance({ids: [this.model.release_version_id]}))
                                                                      ]);
                const attachments                 = await this.app.app.domain.apis.attachments.batch(Models.BatchRequest.newInstance({ids: versionBatch.map(version => version.attachment_id)}));
                this.updateVersionDetails([
                                              this.releaseVersion,
                                              versionResponse
                                          ], attachments, diffSummary);
            }
            this.app.markForCheck();
        }
        finally
        {
            this.versionInfoPromise.resolve();
        }
    }

    public updateDiffSummary(versionDiffSummary: Models.AppSecopsSbomVersionDifference): void
    {
        this.sbomComponentDiffSummary     = {
            total   : this.releaseVersion.component_ids || [],
            upgraded: versionDiffSummary.upgraded_component_ids || [],
            added   : versionDiffSummary.new_component_ids || [],
            removed : versionDiffSummary.deleted_component_ids || [],
            type    : SbomDiffSummaryType.Components
        };
        this.sbomVulnerabilityDiffSummary = {
            total   : this.releaseVersion.vulnerability_ids || [],
            upgraded: null,
            added   : versionDiffSummary.new_vulnerability_ids || [],
            removed : versionDiffSummary.resolved_vulnerability_ids || [],
            type    : SbomDiffSummaryType.Vulnerabilities
        };

        this.sbomVersionVulnerabilityIds = {
            sbomVersionVulnerabilityIds: this.releaseVersion.sbom_version_vulnerability_ids || []
        };
    }

    public async updateMappingDetails(): Promise<void>
    {
        const definition = await this.app.app.domain.apis.appSecOps.getSbomDefinition(this.model.sbom_definition_id);
        this.model       = Models.AppSecopsSbomDefinition.deepClone(definition);
        this.app.markForCheck();
    }
}

export interface SbomDiffSummary
{
    total: string[];
    upgraded: string[];
    added: string[];
    removed: string[];
    type: SbomDiffSummaryType;
}

export interface DiffSummaryLabelValue
{
    label: string;
    key: string;
    value: string[];
}

export enum SbomDiffSummaryType
{
    Components      = "Components",
    Vulnerabilities = "Vulnerabilities"
}

export interface SbomParserStatusInfo
{
    color: string;
    label: string;
    secondaryLabel: string;
    message: string;
}

export interface SbomDates
{
    versionCreated: string;
    generated: string;
}

export interface SbomVersionVulnerabilityIds
{
    sbomVersionVulnerabilityIds: string[];
}
